import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useRetailerAuth } from "../../context/RetailerAuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Building2, Plus, Search, Phone, MapPin, Calendar, Loader2, ExternalLink, Key, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerShops() {
  const navigate = useNavigate();
  const { retailer, fetchRetailerProfile } = useRetailerAuth();
  const [partnerShops, setPartnerShops] = useState([]);
  const [pendingShops, setPendingShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingPartner, setAddingPartner] = useState(false);
  const [subdomain, setSubdomain] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [searching, setSearching] = useState(false);
  const [addMethod, setAddMethod] = useState("code"); // "code" or "subdomain"

  useEffect(() => {
    fetchPartnerShops();
  }, []);

  const fetchPartnerShops = async () => {
    try {
      const response = await axios.get(`${API}/retailer/partner-shops`);
      // Separate active and pending shops
      const allPartners = retailer?.partner_tenants || [];
      const active = response.data.shops;
      const pending = allPartners.filter(p => p.status === "pending").map(p => ({
        id: p.tenant_id,
        name: p.tenant_name,
        status: p.status,
        added_at: p.added_at
      }));
      setPartnerShops(active);
      setPendingShops(pending);
    } catch (error) {
      console.error("Failed to fetch partner shops:", error);
      toast.error("Failed to load partner shops");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBySubdomain = async (e) => {
    e.preventDefault();
    if (!subdomain.trim()) {
      toast.error("Please enter a shop subdomain");
      return;
    }

    setSearching(true);
    try {
      const response = await axios.post(`${API}/retailer/request-partnership/${subdomain.trim().toLowerCase()}`);
      toast.success(response.data.message);
      setSubdomain("");
      setAddingPartner(false);
      fetchPartnerShops();
      fetchRetailerProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add partner shop");
    } finally {
      setSearching(false);
    }
  };

  const handleAddByCode = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }

    setSearching(true);
    try {
      const response = await axios.post(`${API}/retailer/request-partnership-by-code`, {
        invite_code: inviteCode.trim().toUpperCase()
      });
      toast.success(`Partnership added with ${response.data.shop_name}!`);
      setInviteCode("");
      setAddingPartner(false);
      fetchPartnerShops();
      fetchRetailerProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid invite code");
    } finally {
      setSearching(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Repair Shops</h1>
          <p className="text-gray-600">Manage shops you forward repair jobs to</p>
        </div>
        <Button 
          className="bg-orange-600 hover:bg-orange-700"
          onClick={() => setAddingPartner(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Partner Shop
        </Button>
      </div>

      {/* Add Partner Form */}
      {addingPartner && (
        <Card className="border-2 border-orange-200 bg-orange-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Add New Partner Shop</CardTitle>
            <CardDescription>
              Use an invite code from the repair shop, or search by subdomain
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={addMethod} onValueChange={setAddMethod} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="code" className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Invite Code
                </TabsTrigger>
                <TabsTrigger value="subdomain" className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  By Subdomain
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="code">
                <form onSubmit={handleAddByCode} className="flex gap-3">
                  <Input
                    placeholder="Enter 8-character invite code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="flex-1 font-mono text-center tracking-wider"
                    maxLength={8}
                  />
                  <Button type="submit" disabled={searching}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                    Join
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setAddingPartner(false)}>
                    Cancel
                  </Button>
                </form>
                <p className="text-xs text-gray-500 mt-2">
                  Ask the repair shop for their invite code. Using a code grants instant partnership.
                </p>
              </TabsContent>
              
              <TabsContent value="subdomain">
                <form onSubmit={handleAddBySubdomain} className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input
                      placeholder="shopname"
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value)}
                      className="pr-32"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      .aftersales.pro
                    </span>
                  </div>
                  <Button type="submit" disabled={searching}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                    Request
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setAddingPartner(false)}>
                    Cancel
                  </Button>
                </form>
                <p className="text-xs text-gray-500 mt-2">
                  Searching by subdomain may require shop approval before you can forward jobs.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Pending Partnerships */}
      {pendingShops.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-800 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending Approval ({pendingShops.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {pendingShops.map((shop) => (
                <Badge key={shop.id} variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                  {shop.name || "Unnamed Shop"}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-yellow-700 mt-2">
              These shops need to approve your partnership request before you can forward jobs.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Partner Shops List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      ) : partnerShops.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Partner Shops Yet</h3>
            <p className="text-gray-500 mb-4">
              Add a repair shop to start forwarding jobs
            </p>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => setAddingPartner(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Partner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partnerShops.map((shop) => (
            <Card key={shop.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{shop.name}</h3>
                <a 
                  href={`https://${shop.subdomain}.aftersales.pro`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-orange-600 hover:underline flex items-center gap-1 mb-3"
                >
                  {shop.subdomain}.aftersales.pro
                  <ExternalLink className="w-3 h-3" />
                </a>
                
                {shop.address && (
                  <p className="text-sm text-gray-600 flex items-start gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    {shop.address}
                  </p>
                )}
                {shop.phone && (
                  <p className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {shop.phone}
                  </p>
                )}
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Calendar className="w-3 h-3" />
                  Partnered {formatDate(shop.added_at)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
